import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Lock, ShieldCheck, Menu, X, Download,
  Linkedin, Instagram, Youtube,
} from "lucide-react";
import api from "../api";

/*
 * AUPRÉA — Landing v3 (banque privée / étude notariale)
 * Rafonte strictement limitée à Landing.jsx (header/footer inline).
 * Scopée via .landing-premium pour ne pas impacter les autres pages.
 */

const C = {
  navy: "#1B2A4A",
  gold: "#C8902C",
  goldDark: "#8A5F14",
  goldLight: "#E2B865",
  teal: "#1F7A6C",
  cream: "#F5F2EC",
  white: "#FFFFFF",
  gray: "#555555",
  line: "#E4DDD0",
};

const FONT_SERIF = "'Playfair Display', Georgia, serif";
const FONT_SANS = "'Lato', 'Helvetica Neue', Arial, sans-serif";

/* ---------------- Helpers UI ---------------- */
function Kicker({ children, color = C.gold, align = "left" }) {
  const barColor = color;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14, justifyContent: align === "center" ? "center" : "flex-start", marginBottom: 14 }}>
      <span style={{ display: "inline-block", width: 40, height: 1.5, background: barColor }} />
      <span style={{ fontFamily: FONT_SANS, fontSize: 13, fontWeight: 700, letterSpacing: "0.24em", color, textTransform: "uppercase" }}>
        {children}
      </span>
    </div>
  );
}
function BtnPrimary({ to, href, children, testid, external = false }) {
  const styles = {
    display: "inline-flex", alignItems: "center", gap: 10, minHeight: 44,
    background: C.gold, color: C.navy, fontFamily: FONT_SANS, fontSize: 15, fontWeight: 700,
    padding: "18px 32px", border: "none", borderRadius: 4, textDecoration: "none",
    letterSpacing: "0.01em", cursor: "pointer", transition: "transform .15s ease, filter .15s ease",
  };
  const inner = <>{children} <ArrowRight size={18} strokeWidth={1.75} /></>;
  if (href) {
    return <a href={href} target={external ? "_blank" : undefined} rel={external ? "noopener noreferrer" : undefined} data-testid={testid} style={styles}>{inner}</a>;
  }
  return <Link to={to} data-testid={testid} style={styles}>{inner}</Link>;
}
function BtnOutline({ to, href, children, testid, dark = false }) {
  const styles = {
    display: "inline-flex", alignItems: "center", gap: 8, minHeight: 44,
    background: "transparent", color: dark ? C.cream : C.navy,
    fontFamily: FONT_SANS, fontSize: 14.5, fontWeight: 600,
    padding: "11px 22px", border: `1.5px solid ${dark ? C.goldLight : C.navy}`, borderRadius: 4,
    textDecoration: "none", letterSpacing: "0.01em", cursor: "pointer",
  };
  if (href) return <a href={href} data-testid={testid} style={styles}>{children}</a>;
  return <Link to={to} data-testid={testid} style={styles}>{children}</Link>;
}
function LinkUnderlined({ to, href, children, testid, color = C.goldDark }) {
  const styles = {
    fontFamily: FONT_SANS, fontSize: 15, fontWeight: 600, color,
    textDecoration: "underline", textDecorationColor: color, textUnderlineOffset: 4,
    textDecorationThickness: 1.5, display: "inline-flex", alignItems: "center", gap: 6,
  };
  if (href) return <a href={href} data-testid={testid} style={styles}>{children} <ArrowRight size={14} strokeWidth={1.7} /></a>;
  return <Link to={to} data-testid={testid} style={styles}>{children} <ArrowRight size={14} strokeWidth={1.7} /></Link>;
}
function NavLinkTop({ to, children, testid }) {
  return (
    <Link to={to} data-testid={testid} style={{
      fontFamily: FONT_SANS, fontSize: 14.5, fontWeight: 500, color: C.navy,
      textDecoration: "none", padding: "8px 2px", letterSpacing: "0.01em",
    }}>
      {children}
    </Link>
  );
}

/* ---------------- Newsletter ---------------- */
function NewsletterForm() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) { setStatus("invalid"); return; }
    setLoading(true); setStatus("");
    try {
      await api.post("/newsletter", { email: email.trim().toLowerCase(), source: "landing-v3" });
      setStatus("ok"); setEmail("");
    } catch (_e) { setStatus("err"); }
    finally { setLoading(false); }
  };
  if (status === "ok") return <div data-testid="newsletter-ok" style={{ fontSize: 13.5, color: C.goldLight, fontFamily: FONT_SANS }}>Merci — votre inscription est enregistrée.</div>;
  return (
    <form onSubmit={submit} style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
      <input
        type="email" value={email} onChange={(e) => setEmail(e.target.value)}
        placeholder="votre@email.fr" data-testid="newsletter-email"
        style={{
          flex: "1 1 180px", minWidth: 160, padding: "11px 14px", fontSize: 14, fontFamily: FONT_SANS,
          background: "rgba(255,255,255,0.08)", border: `1px solid ${C.goldLight}55`, color: C.cream,
          borderRadius: 3, outline: "none",
        }}
      />
      <button
        type="submit" disabled={loading} data-testid="newsletter-submit"
        style={{
          padding: "11px 22px", fontSize: 13.5, fontWeight: 600, fontFamily: FONT_SANS,
          background: "transparent", color: C.goldLight, border: `1.5px solid ${C.gold}`,
          borderRadius: 3, cursor: loading ? "not-allowed" : "pointer", minHeight: 44,
        }}
      >
        {loading ? "…" : "S'inscrire"}
      </button>
      {status === "invalid" && <div style={{ width: "100%", fontSize: 12, color: "#E2B865" }}>Email invalide.</div>}
      {status === "err" && <div style={{ width: "100%", fontSize: 12, color: "#E2B865" }}>Erreur — réessayez plus tard.</div>}
    </form>
  );
}

/* ================================================================ */
/* PAGE                                                             */
/* ================================================================ */
export default function Landing() {
  const [burger, setBurger] = useState(false);
  const [isMobile, setIsMobile] = useState(() => (typeof window !== "undefined" ? window.innerWidth <= 980 : false));
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 980);
    window.addEventListener("resize", onResize);
    onResize();
    return () => window.removeEventListener("resize", onResize);
  }, []);
  useEffect(() => {
    document.title = "AUPRÉA — La plateforme dédiée aux angles morts de la succession";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) { meta = document.createElement("meta"); meta.name = "description"; document.head.appendChild(meta); }
    const prev = meta.getAttribute("content");
    meta.setAttribute("content", "Le carnet privé qui rassemble vos documents, vos volontés et vos personnes de confiance. Conçu avec la rigueur d'une étude notariale. Gratuit.");
    return () => { if (prev != null) meta.setAttribute("content", prev); };
  }, []);

  return (
    <div className="landing-premium" style={{ background: C.cream, color: C.navy, fontFamily: FONT_SANS, lineHeight: 1.65, fontSize: 16 }}>
      <style>{`
        .landing-premium { font-feature-settings: "liga","dlig","kern"; }
        .landing-premium a { -webkit-tap-highlight-color: transparent; }
        .landing-premium .lp-hr { height: 1px; background: ${C.line}; width: 100%; }
        .landing-premium .lp-fade-in { animation: lpFade .3s ease-out both; }
        @keyframes lpFade { from { opacity: 0; } to { opacity: 1; } }
        .landing-premium .lp-container { max-width: 1200px; margin: 0 auto; padding: 0 24px; }
        .landing-premium h1,.landing-premium h2,.landing-premium h3 { font-family: ${FONT_SERIF}; color: ${C.navy}; font-weight: 500; }
        .landing-premium h1 em,.landing-premium h2 em,.landing-premium h3 em { font-style: italic; font-weight: 500; color: ${C.gold}; }
        .landing-premium p { color: ${C.gray}; font-size: 17px; line-height: 1.7; }
        .landing-premium .lp-section { padding: 110px 0; }
        @media (max-width: 768px) {
          .landing-premium .lp-section { padding: 72px 0; }
          .landing-premium .lp-container { padding: 0 20px; }
        }
      `}</style>

      {/* 1. HEADER */}
      <header style={{ background: C.cream, borderBottom: `1px solid ${C.line}`, position: "sticky", top: 0, zIndex: 50, backdropFilter: "saturate(1.1) blur(2px)" }} data-testid="landing-header">
        <div className="lp-container" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: 76, gap: 16 }}>
          <Link to="/" data-testid="brand-logo" style={{ fontFamily: FONT_SERIF, fontWeight: 700, fontSize: 28, color: C.navy, textDecoration: "none", letterSpacing: "0.02em" }}>AUPRÉA</Link>
          {/* Desktop nav */}
          {!isMobile && (
            <nav style={{ display: "flex", alignItems: "center", gap: 32 }}>
              <NavLinkTop to="/inscription" testid="nav-carnet">Le Carnet</NavLinkTop>
              <NavLinkTop to="/comment-ca-marche" testid="nav-cmm">Comment ça marche</NavLinkTop>
              <NavLinkTop to="/ouvrir-une-succession/diagnostic" testid="nav-succession">Succession en cours</NavLinkTop>
              <NavLinkTop to="/tarifs" testid="nav-tarifs">Tarifs</NavLinkTop>
              <BtnOutline to="/connexion" testid="btn-nav-login">Se connecter</BtnOutline>
            </nav>
          )}
          {/* Mobile burger */}
          {isMobile && (
            <button onClick={() => setBurger(true)} aria-label="Ouvrir le menu" data-testid="btn-burger"
              style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", background: "transparent", border: "none", color: C.navy, padding: 8, cursor: "pointer" }}>
              <Menu size={28} strokeWidth={1.6} />
            </button>
          )}
        </div>
      </header>

      {/* Mobile drawer */}
      {burger && (
        <div onClick={() => setBurger(false)} data-testid="mobile-menu" style={{ position: "fixed", inset: 0, background: "rgba(27,42,74,0.5)", zIndex: 100 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ position: "absolute", top: 0, right: 0, bottom: 0, width: "86%", maxWidth: 340, background: C.cream, padding: "24px 24px", display: "flex", flexDirection: "column", gap: 4 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
              <span style={{ fontFamily: FONT_SERIF, fontWeight: 700, fontSize: 24, color: C.navy }}>AUPRÉA</span>
              <button onClick={() => setBurger(false)} aria-label="Fermer" style={{ background: "transparent", border: "none", color: C.navy, cursor: "pointer" }}><X size={24} strokeWidth={1.6} /></button>
            </div>
            {[
              ["/inscription", "Le Carnet", "m-carnet"],
              ["/comment-ca-marche", "Comment ça marche", "m-cmm"],
              ["/ouvrir-une-succession/diagnostic", "Succession en cours", "m-succ"],
              ["/tarifs", "Tarifs", "m-tarifs"],
              ["/pros", "Pour les professionnels", "m-pros"],
            ].map(([to, lbl, tid]) => (
              <Link key={tid} to={to} onClick={() => setBurger(false)} data-testid={tid} style={{ padding: "14px 2px", borderBottom: `1px solid ${C.line}`, fontFamily: FONT_SANS, fontSize: 16, color: C.navy, textDecoration: "none" }}>{lbl}</Link>
            ))}
            <div style={{ marginTop: 20 }}><BtnPrimary to="/connexion" testid="m-login">Se connecter</BtnPrimary></div>
          </div>
        </div>
      )}

      {/* 2. HERO */}
      <section className="lp-section" style={{ background: C.cream, paddingTop: 90, paddingBottom: 90 }} data-testid="hero">
        <div className="lp-container">
          <div className="lp-hero-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 72, alignItems: "center" }}>
            <div>
              <Kicker>LA PREMIÈRE PLATEFORME DÉDIÉE AUX ANGLES MORTS DE LA SUCCESSION</Kicker>
              <h1 className="lp-fade-in" data-testid="hero-h1" style={{ fontSize: "clamp(42px, 5.6vw, 68px)", lineHeight: 1.08, margin: "0 0 20px 0", fontWeight: 500 }}>
                Transmettez votre patrimoine<br /><em>en toute sérénité.</em>
              </h1>
              <div style={{ fontFamily: FONT_SERIF, fontStyle: "italic", color: C.teal, fontSize: 22, marginBottom: 24 }}>« Anticiper est un acte d'amour. »</div>
              <p style={{ fontSize: 18, maxWidth: 540, marginBottom: 32 }}>
                Un carnet privé en ligne qui rassemble vos documents, vos volontés et vos personnes de confiance — pour que vos proches sachent exactement où chercher.
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 24, alignItems: "center", marginBottom: 20 }}>
                <BtnPrimary to="/inscription" testid="hero-cta-primary">Créer mon carnet gratuitement</BtnPrimary>
                <LinkUnderlined href="#comment-ca-marche" testid="hero-cta-secondary">Voir comment ça marche</LinkUnderlined>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, color: C.goldDark, fontSize: 13.5, fontWeight: 500 }}>
                <Lock size={16} strokeWidth={1.7} />
                <span>Gratuit · Sans carte bancaire · Prêt en moins de 5 minutes</span>
              </div>
            </div>
            {/* Aperçu carnet (carte blanche, pas de carte flottante parasite) */}
            <div className="lp-fade-in" data-testid="hero-preview" style={{ background: C.white, border: `1px solid ${C.line}`, borderRadius: 6, boxShadow: "0 24px 60px -30px rgba(27,42,74,0.25)", padding: "28px 32px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
                <div>
                  <div style={{ fontFamily: FONT_SANS, fontSize: 11, letterSpacing: "0.2em", color: C.goldDark, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>MON CARNET</div>
                  <div style={{ fontFamily: FONT_SERIF, fontSize: 24, color: C.navy }}>Marie Dupont</div>
                </div>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", border: `1px solid ${C.teal}`, color: C.teal, fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", borderRadius: 3 }}>
                  <ShieldCheck size={13} strokeWidth={1.8} /> Sécurisé
                </div>
              </div>
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                  <span style={{ fontSize: 12.5, color: C.gray, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase" }}>Progression</span>
                  <span style={{ fontFamily: FONT_SERIF, fontSize: 20, color: C.gold, fontWeight: 600 }}>68 %</span>
                </div>
                <div style={{ height: 3, background: C.line, borderRadius: 2 }}>
                  <div style={{ width: "68%", height: "100%", background: C.gold, borderRadius: 2 }} />
                </div>
              </div>
              {[
                { lbl: "Documents essentiels", st: "Complet", ok: true },
                { lbl: "Personnes de confiance", st: "Complet", ok: true },
                { lbl: "Volontés exprimées", st: "À compléter", ok: false },
              ].map((row, i) => (
                <div key={row.lbl} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 0", borderTop: i === 0 ? "none" : `1px solid ${C.line}` }}>
                  <span style={{ fontSize: 15, color: C.navy }}>{row.lbl}</span>
                  <span style={{ fontSize: 12.5, color: row.ok ? C.teal : C.goldDark, fontWeight: 600, letterSpacing: "0.03em" }}>{row.st}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <style>{`
          @media (max-width: 980px) {
            .landing-premium .lp-hero-grid { grid-template-columns: 1fr !important; gap: 44px !important; }
          }
        `}</style>
      </section>

      {/* 3. BANDE DE RECONNAISSANCE */}
      <section style={{ background: C.white, borderTop: `1px solid ${C.line}`, borderBottom: `1px solid ${C.line}`, padding: "28px 0" }} data-testid="recognition">
        <div className="lp-container" style={{ display: "flex", gap: "clamp(16px, 2.5vw, 36px)", flexWrap: "wrap", justifyContent: "center", alignItems: "center" }}>
          <span style={{ fontFamily: FONT_SANS, fontSize: 11.5, fontWeight: 700, letterSpacing: "0.24em", textTransform: "uppercase", color: C.goldDark }}>Ils nous ont distingués</span>
          {[
            "Lancement à l'Assemblée nationale",
            "Prix de l'Innovation Outre-mer",
            "Prix RIDD · Côte d'Ivoire",
            "France-Antilles",
            "France-Guyane",
          ].map((lbl) => (
            <span key={lbl} style={{ fontFamily: FONT_SERIF, fontSize: 17, color: C.navy }}>{lbl}</span>
          ))}
        </div>
      </section>

      {/* 4. ANGLES MORTS */}
      <section className="lp-section" style={{ background: C.cream }} data-testid="angles-morts">
        <div className="lp-container">
          <div style={{ maxWidth: 920, marginBottom: 72 }}>
            <Kicker color={C.teal}>LES ANGLES MORTS</Kicker>
            <h2 style={{ fontSize: "clamp(32px, 3.8vw, 48px)", lineHeight: 1.15, margin: 0, maxWidth: 820 }}>
              Ce n'est pas la succession qui divise les familles.<br />
              <em>C'est ce qui n'a pas été préparé.</em>
            </h2>
          </div>
          <div className="lp-angles-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 0, borderTop: `2px solid ${C.navy}` }}>
            {[
              { n: "I.", t: "Des documents introuvables", d: "Actes, contrats, comptes, titres de propriété : centralisés au même endroit, partagés en lecture seule avec votre notaire." },
              { n: "II.", t: "Des volontés jamais dites", d: "Vos souhaits exprimés clairement, rubrique par rubrique, pour que personne n'ait à les deviner." },
              { n: "III.", t: "Des proches non identifiés", d: "Les personnes de confiance désignées à l'avance, et prévenues au bon moment." },
            ].map((it, i) => (
              <div key={it.n} style={{ padding: "48px 36px", borderRight: i < 2 ? `1px solid ${C.line}` : "none" }}>
                <div style={{ fontFamily: FONT_SERIF, fontSize: 42, color: C.gold, fontWeight: 600, marginBottom: 20, lineHeight: 1 }}>{it.n}</div>
                <h3 style={{ fontSize: 23, margin: "0 0 14px 0", fontWeight: 500, lineHeight: 1.3 }}>{it.t}</h3>
                <p style={{ fontSize: 16, margin: 0, lineHeight: 1.65 }}>{it.d}</p>
              </div>
            ))}
          </div>
        </div>
        <style>{`
          @media (max-width: 900px) {
            .landing-premium .lp-angles-grid { grid-template-columns: 1fr !important; }
            .landing-premium .lp-angles-grid > div { border-right: none !important; border-bottom: 1px solid ${C.line}; }
          }
        `}</style>
      </section>

      {/* 5. COMMENT ÇA MARCHE */}
      <section id="comment-ca-marche" className="lp-section" style={{ background: C.white }} data-testid="comment-ca-marche">
        <div className="lp-container">
          <div className="lp-cmm-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1.1fr", gap: 72, alignItems: "flex-start" }}>
            <div>
              <Kicker color={C.teal}>LA MÉTHODE</Kicker>
              <h2 style={{ fontSize: "clamp(32px, 3.6vw, 44px)", lineHeight: 1.15, margin: "0 0 18px 0" }}>
                Trois étapes,<br /><em>à votre rythme.</em>
              </h2>
              <p style={{ fontSize: 17, maxWidth: 440 }}>
                Conçu avec la rigueur d'une étude notariale, pensé pour être simple dès la première minute.
              </p>
            </div>
            <div>
              {[
                { n: "1", t: "Créez votre carnet", d: "Gratuit, sans engagement, en moins de cinq minutes." },
                { n: "2", t: "Complétez-le, rubrique par rubrique", d: "Onze rubriques guidées et des alertes quand une information n'est plus à jour." },
                { n: "3", t: "Transmettez l'accès", d: "Vos personnes de confiance et votre notaire y accèdent, en toute sécurité, le moment venu." },
              ].map((step, i) => (
                <div key={step.n} style={{ display: "grid", gridTemplateColumns: "70px 1fr", gap: 28, padding: "28px 0", borderTop: i === 0 ? "none" : `1px solid ${C.line}` }}>
                  <div style={{ fontFamily: FONT_SERIF, fontSize: 54, color: C.gold, fontWeight: 500, lineHeight: 1 }}>{step.n}</div>
                  <div>
                    <h3 style={{ fontSize: 21, margin: "0 0 8px 0", fontWeight: 500 }}>{step.t}</h3>
                    <p style={{ fontSize: 16, margin: 0, lineHeight: 1.6 }}>{step.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <style>{`
          @media (max-width: 900px) {
            .landing-premium .lp-cmm-grid { grid-template-columns: 1fr !important; gap: 36px !important; }
          }
        `}</style>
      </section>

      {/* 6. OÙ EN ÊTES-VOUS ? */}
      <section className="lp-section" style={{ background: C.cream }} data-testid="ou-en-etes-vous">
        <div className="lp-container">
          <div style={{ textAlign: "center", marginBottom: 56 }}>
            <Kicker align="center">OÙ EN ÊTES-VOUS ?</Kicker>
            <h2 style={{ fontSize: "clamp(32px, 3.6vw, 44px)", lineHeight: 1.15, margin: 0 }}>
              Un chemin différent<br /><em>selon votre situation.</em>
            </h2>
          </div>
          <div className="lp-choix-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
            {/* Carte 1 — Anticiper */}
            <div style={{ background: C.navy, color: C.cream, padding: "48px 40px", borderRadius: 4 }} data-testid="choix-anticiper">
              <Kicker color={C.goldLight}>VOUS SOUHAITEZ ANTICIPER</Kicker>
              <h3 style={{ fontSize: 26, color: C.cream, margin: "0 0 16px 0", fontWeight: 500 }}>Le Carnet d'Inventaire Patrimonial</h3>
              <p style={{ color: "rgba(245,242,236,0.78)", fontSize: 16, marginBottom: 28 }}>
                Tout ce qui compte, au même endroit. Le forfait Découverte est 100 % gratuit, sans carte bancaire.
              </p>
              <BtnPrimary to="/inscription" testid="choix-cta-carnet">Créer mon carnet gratuitement</BtnPrimary>
            </div>
            {/* Carte 2 — Succession en cours */}
            <div style={{ background: C.white, color: C.navy, padding: "48px 40px", borderRadius: 4, border: `1px solid ${C.line}` }} data-testid="choix-succession">
              <Kicker color={C.teal}>UNE SUCCESSION EST EN COURS</Kicker>
              <h3 style={{ fontSize: 26, margin: "0 0 16px 0", fontWeight: 500 }}>Le diagnostic succession</h3>
              <p style={{ fontSize: 16, marginBottom: 28 }}>
                En quatre étapes guidées, sachez quels documents réunir et quels professionnels consulter.
                Diagnostic gratuit · feuille de route complète à 89 €.
              </p>
              <LinkUnderlined to="/ouvrir-une-succession/diagnostic" testid="choix-cta-succession" color={C.teal}>Commencer le diagnostic</LinkUnderlined>
            </div>
          </div>
        </div>
        <style>{`
          @media (max-width: 820px) {
            .landing-premium .lp-choix-grid { grid-template-columns: 1fr !important; }
          }
        `}</style>
      </section>

      {/* 7. CONFIDENTIALITÉ */}
      <section className="lp-section" style={{ background: C.white }} data-testid="confidentialite">
        <div className="lp-container">
          <div style={{ maxWidth: 820, marginBottom: 56 }}>
            <Kicker color={C.teal}>CONFIDENTIALITÉ</Kicker>
            <h2 style={{ fontSize: "clamp(30px, 3.4vw, 40px)", lineHeight: 1.2, margin: "0 0 18px 0" }}>
              Vos données <em>restent en France.</em>
            </h2>
            <p style={{ fontSize: 17 }}>
              Exportez ou supprimez vos informations à tout moment. Personne d'autre que vous ne décide qui y accède.
            </p>
          </div>
          <div className="lp-trust-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 24 }}>
            {[
              { t: "Hébergement en France", icon: ShieldCheck },
              { t: "Conforme RGPD", icon: ShieldCheck },
              { t: "Données chiffrées", icon: ShieldCheck },
              { t: "Export et suppression à la demande", icon: Download },
            ].map((it) => {
              const Icon = it.icon;
              return (
                <div key={it.t} style={{ padding: "28px 24px", border: `1px solid ${C.line}`, borderRadius: 4, background: C.cream }}>
                  <Icon size={28} strokeWidth={1.6} color={C.teal} />
                  <div style={{ marginTop: 14, fontFamily: FONT_SERIF, fontSize: 17, color: C.navy, lineHeight: 1.35 }}>{it.t}</div>
                </div>
              );
            })}
          </div>
        </div>
        <style>{`
          @media (max-width: 900px) {
            .landing-premium .lp-trust-grid { grid-template-columns: 1fr 1fr !important; }
          }
          @media (max-width: 560px) {
            .landing-premium .lp-trust-grid { grid-template-columns: 1fr !important; }
          }
        `}</style>
      </section>

      {/* 8. CTA FINAL */}
      <section style={{ background: C.navy, padding: "120px 0", textAlign: "center" }} data-testid="cta-final">
        <div className="lp-container">
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 24 }}>
            <span style={{ display: "inline-block", width: 40, height: 1.5, background: C.gold }} />
          </div>
          <h2 style={{ fontSize: "clamp(32px, 4vw, 48px)", lineHeight: 1.2, color: C.cream, margin: "0 0 20px 0", fontWeight: 500 }}>
            Le patrimoine se transmet.<br />
            <em style={{ color: C.goldLight }}>La sérénité aussi.</em>
          </h2>
          <p style={{ color: "rgba(245,242,236,0.78)", fontSize: 17, maxWidth: 540, margin: "0 auto 36px" }}>
            Commencez votre carnet en moins de cinq minutes. Gratuit, sans engagement.
          </p>
          <BtnPrimary to="/inscription" testid="cta-final-primary">Créer mon carnet gratuitement</BtnPrimary>
        </div>
      </section>

      {/* 9. FOOTER */}
      <footer style={{ background: C.navy, color: C.cream, padding: "72px 0 36px" }} data-testid="landing-footer">
        <div className="lp-container">
          <div className="lp-footer-grid" style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr 1fr 1.4fr", gap: 48, marginBottom: 56 }}>
            {/* Col 1 */}
            <div>
              <div style={{ fontFamily: FONT_SERIF, fontWeight: 700, fontSize: 26, color: C.cream, marginBottom: 12, letterSpacing: "0.02em" }}>AUPRÉA</div>
              <div style={{ fontFamily: FONT_SERIF, fontStyle: "italic", color: C.goldLight, fontSize: 16, marginBottom: 20 }}>« Anticiper est un acte d'amour. »</div>
              <div style={{ fontSize: 12, letterSpacing: "0.22em", color: "rgba(245,242,236,0.55)", fontWeight: 600 }}>OUTREMER · FRANCE</div>
            </div>
            {/* Col 2 — AUPRÉA */}
            <div>
              <div style={{ fontSize: 12, letterSpacing: "0.22em", color: C.goldLight, fontWeight: 700, textTransform: "uppercase", marginBottom: 16 }}>AUPRÉA</div>
              <FooterLink to="/inscription" testid="footer-link-carnet">Le Carnet</FooterLink>
              <FooterLink to="/tarifs" testid="footer-link-tarifs">Tarifs</FooterLink>
              <FooterLink to="/ouvrir-une-succession/diagnostic" testid="footer-link-succession">Succession en cours</FooterLink>
              <FooterLink to="/pros" testid="footer-link-pros">Pour les professionnels</FooterLink>
            </div>
            {/* Col 3 — Compte */}
            <div>
              <div style={{ fontSize: 12, letterSpacing: "0.22em", color: C.goldLight, fontWeight: 700, textTransform: "uppercase", marginBottom: 16 }}>Compte</div>
              <FooterLink to="/connexion" testid="footer-link-connexion">Se connecter</FooterLink>
              <FooterLink to="/acces-heritier" testid="footer-link-heritier">Accès héritier</FooterLink>
              <FooterLink to="/contact" testid="footer-link-contact">Contact</FooterLink>
            </div>
            {/* Col 4 — Newsletter */}
            <div>
              <div style={{ fontSize: 12, letterSpacing: "0.22em", color: C.goldLight, fontWeight: 700, textTransform: "uppercase", marginBottom: 16 }}>Newsletter</div>
              <div style={{ fontFamily: FONT_SERIF, fontSize: 17, color: C.cream, marginBottom: 6 }}>Le Carnet</div>
              <div style={{ fontSize: 14, color: "rgba(245,242,236,0.7)", lineHeight: 1.55 }}>Succession, patrimoine et transmission — l'essentiel, sans jargon.</div>
              <NewsletterForm />
            </div>
          </div>

          <div style={{ borderTop: `1px solid rgba(226,184,101,0.2)`, paddingTop: 24, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 24, flexWrap: "wrap" }}>
            <div style={{ fontSize: 13, color: "rgba(245,242,236,0.6)", display: "flex", gap: 20, flexWrap: "wrap" }}>
              <span>© 2026 AUPRÉA SAS</span>
              <Link to="/legal/mentions-legales" data-testid="footer-link-mentions" style={legalLink}>Mentions légales</Link>
              <Link to="/legal/cgu" data-testid="footer-link-cgu" style={legalLink}>CGU</Link>
              <Link to="/legal/cgv" data-testid="footer-link-cgv" style={legalLink}>CGV</Link>
              <Link to="/legal/confidentialite" data-testid="footer-link-confidentialite" style={legalLink}>Confidentialité</Link>
            </div>
            <div style={{ display: "flex", gap: 10 }} data-testid="social-links">
              <SocialCircle href="https://www.linkedin.com/company/auprea" label="LinkedIn" testid="social-linkedin"><Linkedin size={16} strokeWidth={1.6} /></SocialCircle>
              <SocialCircle href="https://www.instagram.com/auprea.officiel" label="Instagram" testid="social-instagram"><Instagram size={16} strokeWidth={1.6} /></SocialCircle>
              <SocialCircle href="https://www.youtube.com/@auprea" label="YouTube" testid="social-youtube"><Youtube size={16} strokeWidth={1.6} /></SocialCircle>
              <SocialCircle href="https://www.tiktok.com/@auprea" label="TikTok" testid="social-tiktok">
                {/* TikTok n'est pas dans Lucide : trait or stylisé */}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.goldLight} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M9 12.5a3.5 3.5 0 1 0 3.5 3.5V3c1 2.5 3 4 5.5 4" /></svg>
              </SocialCircle>
            </div>
          </div>
        </div>
        <style>{`
          @media (max-width: 980px) {
            .landing-premium .lp-footer-grid { grid-template-columns: 1fr 1fr !important; }
          }
          @media (max-width: 560px) {
            .landing-premium .lp-footer-grid { grid-template-columns: 1fr !important; }
          }
        `}</style>
      </footer>
    </div>
  );
}

const legalLink = { color: "rgba(245,242,236,0.6)", textDecoration: "none", fontSize: 13 };

function FooterLink({ to, href, children, testid }) {
  const style = { display: "block", padding: "7px 0", color: "rgba(245,242,236,0.75)", textDecoration: "none", fontSize: 14.5, fontFamily: FONT_SANS };
  if (href) return <a href={href} data-testid={testid} style={style}>{children}</a>;
  return <Link to={to} data-testid={testid} style={style}>{children}</Link>;
}
function SocialCircle({ href, children, label, testid }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" aria-label={label} data-testid={testid}
      style={{ width: 36, height: 36, borderRadius: "50%", border: `1px solid ${C.goldLight}60`, color: C.goldLight, display: "inline-flex", alignItems: "center", justifyContent: "center", transition: "border-color .15s" }}
    >
      {children}
    </a>
  );
}
